import React, { useEffect, useState } from 'react';
import { loginMFA } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { getUrlParams } from '../services/parameter.service';
import Feedback from '../components/Feedback';

const LoginMFA: React.FC = () => {
  const [code, setCode] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [rememberMachine, setRememberMachine] = useState(true);
  const [message, setMessage] = useState('');
  const [errorMessages, setErrorMessage] = useState([] as string[]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const { returnUrl, forwardUrl, rememberMeValue } = getUrlParams();

  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (rememberMeValue) {
      setRememberMe(!!rememberMeValue);
    }
  }, []);

  const isValidReturnUrl = (url: string) => {
    // Ensure it starts with a slash and is not a full URL with a protocol (http/https)
    return url && url.startsWith('/') && !url.startsWith('//');
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await loginMFA(code, rememberMachine, rememberMe, false);
      if (response.success) {
        if (returnUrl && isValidReturnUrl(returnUrl)) {
          window.location.href = returnUrl;
        } else if (forwardUrl && isValidReturnUrl(forwardUrl)) {
          window.location.href = forwardUrl;
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        setMessage('');
        setErrorMessage(response.errors);
        setLoading(false);
      }
    } catch (error) {
      setMessage('');
      setErrorMessage(['Invalid code']);
      setLoading(false);
    }
  };

  if (isLoggedIn && !returnUrl && !forwardUrl) {
    return <></>;
  }

  return (
    <div>
      <h2>Two-factor authentication</h2>
      <p>
        Your login is protected with an authenticator app. Enter your
        authenticator code below.
      </p>
      <form onSubmit={handleLogin} className="login-form">
        <div className="field">
          <input
            type="text"
            id="code"
            placeholder="Verification code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>

        <div className="checkbox-container">
          <input
            type="checkbox"
            id="rememberMachine"
            checked={rememberMachine}
            onChange={(e) => setRememberMachine(e.target.checked)}
          />
          <label htmlFor="rememberMachine">Remember this machine</label>
        </div>
        <div className="button-container">
          <Button className="action-button" isLoading={loading} isSubmit={true}>
            Login
          </Button>
          <Button type="Outline" onClick={() => navigate('/')}>
            Cancel
          </Button>
        </div>
        <p>
          Don&apos;t have access to your authenticator device? You can{' '}
          <Link
            to={
              returnUrl
                ? `/Account/LoginMFARecoveryCode?returnUrl=${returnUrl}`
                : forwardUrl
                  ? `/Account/LoginMFARecoveryCode?forwardUrl=${forwardUrl}`
                  : `/Account/LoginMFARecoveryCode`
            }
          >
            log in with a recovery code.
          </Link>
        </p>
        <Feedback message={message} type="success" />
        <Feedback messages={errorMessages} type="error" />
        <div className="links">
          <Link to="/Policies/terms-of-use">Terms of Use</Link>|
          <Link to="/Policies/privacy-policy">Privacy Policy</Link>
        </div>
      </form>
    </div>
  );
};

export default LoginMFA;
